"use client";
import { create } from "zustand";
import { persist } from "zustand/middleware"; 
import type { Order } from "@/types/inde_menu";
import { useOrderStore } from "./useOrderStore";
import { toast } from "sonner";
import SocketService from "@/lib/socket/SocketService";
import { useEffect } from "react";
export interface SplitParticipant {
    id: string;
    name: string;
    amount: number;
    status: "pending" | "accepted" | "declined" | "paid";
}
export interface PaymentRequest {
    id: string;
    orderId: string;
    initiatorId: string;
    recipientId: string;
    amount: number;
    status: "pending" | "accepted" | "declined" | "paid";
    createdAt: string;
    expiresAt: string;
    splitCode: string;
    orderDetails?: Order | null;
}

export interface SplitRequest {
    id: string;
    orderId: string;
    initiatorId: string;
    participants: SplitParticipant[];
    status: "pending" | "completed" | "expired";
    expiresAt: Date;
    createdAt: Date;
    splitCode: string;
}

interface PaymentRequestStore {
    incomingRequests: PaymentRequest[];
    outgoingRequests: SplitRequest[];
    activeRequest: PaymentRequest | null;
    showModal: boolean;

    // Actions
    addIncomingRequest: (request: PaymentRequest) => void;
    addOutgoingRequest: (request: SplitRequest) => void;
    updateIncomingRequest: (id: string, updates: Partial<PaymentRequest>) => void;
    updateOutgoingRequest: (id: string, updates: Partial<SplitRequest>) => void;
    removeIncomingRequest: (id: string) => void;
    removeOutgoingRequest: (id: string) => void;
    setActiveRequest: (request: PaymentRequest | null) => void;
    setShowModal: (show: boolean) => void;

    // Business logic
    acceptPaymentRequest: (id: string) => Promise<void>;
    declinePaymentRequest: (id: string) => Promise<void>;
    cancelSplitRequest: (id: string) => Promise<void>;

    // Socket events
    sendPaymentRequest: (initiatorId: string, recipientIds: string[], orderId: string, amounts: Record<string, number>) => Promise<void>;
    notifyPaymentReceived: (requestId: string, recipientId: string) => void;
    notifyPaymentDeclined: (requestId: string, recipientId: string) => void;
}

export const usePaymentRequestStore = create<PaymentRequestStore>()(
    persist(
        (set, get) => ({
            incomingRequests: [],
            outgoingRequests: [],
            activeRequest: null,
            showModal: false,
addIncomingRequest: (request) => {
                set(state => {
                    if (state.incomingRequests.some(r => r.id === request.id)) return state;
                    return { ...state, incomingRequests: [...state.incomingRequests, request], activeRequest: request, showModal: true };
                });
            },

            addOutgoingRequest: (request) => {
                set(state => {
                    if (state.outgoingRequests.some(r => r.id === request.id)) return state;
                    return { ...state, outgoingRequests: [...state.outgoingRequests, request] };
                });
            },

            updateIncomingRequest: (id, updates) => {
                set(state => ({
                    ...state,
                    incomingRequests: state.incomingRequests.map(r => r.id === id ? { ...r, ...updates } : r),
                    activeRequest: state.activeRequest?.id === id ? { ...state.activeRequest, ...updates } : state.activeRequest,
                }));
            },

            updateOutgoingRequest: (id, updates) => {
                set(state => ({
                    ...state,
                    outgoingRequests: state.outgoingRequests.map(r => r.id === id ? { ...r, ...updates } : r),
                }));
            },

            removeIncomingRequest: (id) => {
                set(state => {
                    const newRequests = state.incomingRequests.filter(r => r.id !== id);
                    const newState: Partial<PaymentRequestStore> = { incomingRequests: newRequests };
                    if (state.activeRequest?.id === id) { newState.activeRequest = newRequests[0] || null; if (!newRequests[0]) newState.showModal = false; }
                    return { ...state, ...newState };
                });
            },

            removeOutgoingRequest: (id) => set(state => ({ ...state, outgoingRequests: state.outgoingRequests.filter(r => r.id !== id) })),
            setActiveRequest: (request) => set({ activeRequest: request }),
            setShowModal: (show) => set({ showModal: show }),

            acceptPaymentRequest: async (id) => { /* same as before */ return Promise.resolve(); },
            declinePaymentRequest: async (id) => { /* same as before */ return Promise.resolve(); },
            cancelSplitRequest: async (id) => { /* same as before */ return Promise.resolve(); },

            sendPaymentRequest: async (initiatorId, recipientIds, orderId, amounts) => {
                const { fetchOrderById } = useOrderStore.getState();
                const socketService = SocketService.getInstance();
                try {
                    const order = await fetchOrderById(orderId); if (!order) throw new Error("Order not found");
                    const requestId = `split_${Date.now()}`;
                    const splitCode = `SPLIT-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
                    const now = new Date(); const expiresAt = new Date(now.getTime() + 10 * 60 * 1000);

                    const outgoingRequest: SplitRequest = {
                        id: requestId, orderId, initiatorId,
                        participants: Object.entries(amounts).map(([recipientId, amount]) => ({ id: recipientId, name: "Participant", amount, status: "pending" })),
                        status: "pending", createdAt: now, expiresAt, splitCode
                    };
                    get().addOutgoingRequest(outgoingRequest);

                    for (const recipientId of recipientIds) {
                        if (!amounts[recipientId]) continue;
                        const paymentRequest: PaymentRequest = {
                            id: `${requestId}_${recipientId}`,
                            orderId,
                            initiatorId,
                            recipientId,
                            amount: amounts[recipientId],
                            status: "pending",
                            createdAt: now.toISOString(),
                            expiresAt: expiresAt.toISOString(),
                            splitCode,
                            orderDetails: order
                        };
                        socketService.emit("splitPaymentRequest", paymentRequest, [recipientId]);
                    }

                    toast.success(`Split payment requests sent to ${recipientIds.length} participant(s)`);
                    return Promise.resolve();
                } catch (error) {
                    console.error("Error sending payment requests:", error);
                    toast.error("Failed to send payment requests");
                    return Promise.reject(error);
                }
            },

            notifyPaymentReceived: (_requestId, recipientId) => {
                // Find the corresponding outgoing request
                const outgoingRequest = get().outgoingRequests.find(req =>
                    req.participants.some(p => p.id === recipientId)
                );

                if (outgoingRequest) {
                    // Update the participant status
                    get().updateOutgoingRequest(outgoingRequest.id, {
                        participants: outgoingRequest.participants.map(p =>
                            p.id === recipientId ? { ...p, status: "paid" } : p
                        )
                    });

                    // Check if all participants have paid
                    const updatedRequest = get().outgoingRequests.find(req => req.id === outgoingRequest.id);
                    const allPaid = updatedRequest?.participants.every(p =>
                        p.status === "paid" || p.id === updatedRequest.initiatorId
                    );

                    if (allPaid) {
                        get().updateOutgoingRequest(outgoingRequest.id, { status: "completed" });
                        toast.success("All payments received! Order completed.");
                    } else {
                        toast.success(`Payment received from participant ${recipientId}`);
                    }
                }
            },

            notifyPaymentDeclined: (requestId, recipientId) => {
                // Find the corresponding outgoing request
                const outgoingRequest = get().outgoingRequests.find(req =>
                    req.participants.some(p => p.id === recipientId)
                );

                if (outgoingRequest) {
                    // Update the participant status
                    get().updateOutgoingRequest(outgoingRequest.id, {
                        participants: outgoingRequest.participants.map(p =>
                            p.id === recipientId ? { ...p, status: "declined" } : p
                        )
                    });

                    toast.error(`Participant ${recipientId} declined the payment request`);
                }
            },
        }),
        {
            name: "payment-request-storage",
            // Only persist incoming requests to avoid duplicates when rehydrating
            partialize: (state) => ({
                incomingRequests: state.incomingRequests,
            }),
        }
    )
);

// Set up socket listeners
export function useInitPaymentRequestListeners() {
    const { addIncomingRequest, updateIncomingRequest, setShowModal, removeIncomingRequest } = usePaymentRequestStore();

    useEffect(() => {
        const socketService = SocketService.getInstance();
        const handleSplitPaymentRequest = (req: PaymentRequest) => {
            addIncomingRequest(req);
            setShowModal(true);
            toast.info(`New split payment request from ${req.initiatorId}`);
        };
    // Listen for incoming split payment requests
    socketService.on("splitPaymentRequest", handleSplitPaymentRequest);

        // Listen for split request cancellations
        socketService.on("splitRequestCancelled", (data: { requestId: string, message: string }) => {
            updateIncomingRequest(data.requestId, { status: "declined" });
            toast.error(data.message);

            // Remove the request after a delay
            setTimeout(() => {
                removeIncomingRequest(data.requestId);
            }, 5000);
        });

        // Listen for request expiration events
        socketService.on("splitRequestExpired", (data: { requestId: string }) => {
            updateIncomingRequest(data.requestId, { status: "declined" });
            toast.error("The split payment request has expired.");

            // Remove the request after a delay
            setTimeout(() => {
                removeIncomingRequest(data.requestId);
            }, 5000);
        });

        // Listen for payment responses
        socketService.on("splitPaymentResponse", (data: { requestId: string, recipientId: string, accepted: boolean }) => {
            // This is handled by the initiator's client
            const { notifyPaymentReceived, notifyPaymentDeclined } = usePaymentRequestStore.getState();
            
            if (data.accepted) {
                notifyPaymentReceived(data.requestId, data.recipientId);
            } else {
                notifyPaymentDeclined(data.requestId, data.recipientId);
            }
        });

        return () => {
            socketService.off("splitPaymentRequest");
            socketService.off("splitRequestCancelled");
            socketService.off("splitRequestExpired");
            socketService.off("splitPaymentResponse");

            socketService.off("splitPaymentRequest", handleSplitPaymentRequest);
        };
    }, [addIncomingRequest, , setShowModal, updateIncomingRequest, removeIncomingRequest]);

    return null;
}